import {
  OILY_DRY_QUESTIONS,
  SENSITIVE_RESISTANT_QUESTIONS,
  PIGMENTED_QUESTIONS,
  WRINKLED_QUESTIONS,
} from '@lib/survey-questions'
import type { FormState, SectionAnswers } from '@lib/models/types'
import type { AxisKey } from '@lib/survey-constants'

type AnswersKey = keyof Pick<
  FormState,
  'oilyDryAnswers' | 'sensitiveResistantAnswers' | 'pigmentedAnswers' | 'wrinkledAnswers'
>

const AXIS_QUESTION_LISTS = {
  oilyDry: OILY_DRY_QUESTIONS,
  sensitiveResistant: SENSITIVE_RESISTANT_QUESTIONS,
  pigmented: PIGMENTED_QUESTIONS,
  wrinkled: WRINKLED_QUESTIONS,
} as const

export function getAxisPage(axis: AxisKey): number {
  switch (axis) {
    case 'oilyDry':
      return 3
    case 'sensitiveResistant':
      return 4
    case 'pigmented':
      return 5
    case 'wrinkled':
      return 6
  }
}

export function getAxisAnswersKey(axis: AxisKey): AnswersKey {
  switch (axis) {
    case 'oilyDry':
      return 'oilyDryAnswers'
    case 'sensitiveResistant':
      return 'sensitiveResistantAnswers'
    case 'pigmented':
      return 'pigmentedAnswers'
    case 'wrinkled':
      return 'wrinkledAnswers'
  }
}

export function getAxisQuestions(axis: AxisKey) {
  switch (axis) {
    case 'oilyDry':
      return AXIS_QUESTION_LISTS.oilyDry
    case 'sensitiveResistant':
      return AXIS_QUESTION_LISTS.sensitiveResistant
    case 'pigmented':
      return AXIS_QUESTION_LISTS.pigmented
    case 'wrinkled':
      return AXIS_QUESTION_LISTS.wrinkled
  }
}

export function getAxisFromPage(page: number): AxisKey | undefined {
  switch (page) {
    case 3:
      return 'oilyDry'
    case 4:
      return 'sensitiveResistant'
    case 5:
      return 'pigmented'
    case 6:
      return 'wrinkled'
    default:
      return undefined
  }
}

export function getFormStateAnswers(formState: FormState, axis: AxisKey): SectionAnswers {
  switch (axis) {
    case 'oilyDry':
      return formState.oilyDryAnswers
    case 'sensitiveResistant':
      return formState.sensitiveResistantAnswers
    case 'pigmented':
      return formState.pigmentedAnswers
    case 'wrinkled':
      return formState.wrinkledAnswers
  }
}

export function withAxisPrefillAnswers(
  prev: FormState,
  axis: AxisKey,
  seed: SectionAnswers,
): FormState {
  switch (axis) {
    case 'oilyDry':
      return { ...prev, oilyDryAnswers: { ...seed } }
    case 'sensitiveResistant':
      return { ...prev, sensitiveResistantAnswers: { ...seed } }
    case 'pigmented':
      return { ...prev, pigmentedAnswers: { ...seed } }
    case 'wrinkled':
      return { ...prev, wrinkledAnswers: { ...seed } }
  }
}

function mergeSectionAnswer(answers: SectionAnswers, key: string, value: string): SectionAnswers {
  const entries = Object.entries(answers).filter(([k]) => k !== key)
  entries.push([key, value])
  return Object.fromEntries(entries)
}

export function withSectionAnswer(
  prev: FormState,
  section: AnswersKey,
  key: string,
  value: string,
): FormState {
  switch (section) {
    case 'oilyDryAnswers':
      return { ...prev, oilyDryAnswers: mergeSectionAnswer(prev.oilyDryAnswers, key, value) }
    case 'sensitiveResistantAnswers':
      return {
        ...prev,
        sensitiveResistantAnswers: mergeSectionAnswer(prev.sensitiveResistantAnswers, key, value),
      }
    case 'pigmentedAnswers':
      return { ...prev, pigmentedAnswers: mergeSectionAnswer(prev.pigmentedAnswers, key, value) }
    case 'wrinkledAnswers':
      return { ...prev, wrinkledAnswers: mergeSectionAnswer(prev.wrinkledAnswers, key, value) }
  }
}

export function withSkinKnowledge(
  prev: FormState,
  axis: keyof FormState['skinKnowledge'],
  value: boolean,
): FormState {
  switch (axis) {
    case 'oilyDry':
      return { ...prev, skinKnowledge: { ...prev.skinKnowledge, oilyDry: value } }
    case 'sensitiveResistant':
      return { ...prev, skinKnowledge: { ...prev.skinKnowledge, sensitiveResistant: value } }
    case 'pigmented':
      return { ...prev, skinKnowledge: { ...prev.skinKnowledge, pigmented: value } }
    case 'wrinkled':
      return { ...prev, skinKnowledge: { ...prev.skinKnowledge, wrinkled: value } }
  }
}

export function withSliderValue(
  prev: FormState,
  axis: keyof FormState['sliderValues'],
  value: number,
): FormState {
  switch (axis) {
    case 'oilyDry':
      return { ...prev, sliderValues: { ...prev.sliderValues, oilyDry: value } }
    case 'sensitiveResistant':
      return { ...prev, sliderValues: { ...prev.sliderValues, sensitiveResistant: value } }
    case 'pigmented':
      return { ...prev, sliderValues: { ...prev.sliderValues, pigmented: value } }
    case 'wrinkled':
      return { ...prev, sliderValues: { ...prev.sliderValues, wrinkled: value } }
  }
}

export function getSkinKnowledgeValue(
  skinKnowledge: FormState['skinKnowledge'],
  axis: keyof FormState['skinKnowledge'],
): boolean | null {
  switch (axis) {
    case 'oilyDry':
      return skinKnowledge.oilyDry
    case 'sensitiveResistant':
      return skinKnowledge.sensitiveResistant
    case 'pigmented':
      return skinKnowledge.pigmented
    case 'wrinkled':
      return skinKnowledge.wrinkled
  }
}

export function getPrefilledAnswer(
  prefilled: Partial<Record<AxisKey, SectionAnswers>> | undefined,
  axis: AxisKey,
): SectionAnswers | undefined {
  if (!prefilled) return undefined
  switch (axis) {
    case 'oilyDry':
      return prefilled.oilyDry
    case 'sensitiveResistant':
      return prefilled.sensitiveResistant
    case 'pigmented':
      return prefilled.pigmented
    case 'wrinkled':
      return prefilled.wrinkled
  }
}
